import { CustomError } from 'pricing-pages-lib/errors/CustomErrors';
import { useMemo } from 'react';
import { CORE_SEATS_ENTERPRISE, CORE_SEATS_PROFESSIONAL, CORE_SEATS_STARTER } from 'self-service-api/constants/ApiNames';
import FeatureTable from 'ui-addon-upgrades/featureTable/FeatureTable';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { pinKeyFeatures } from 'pricing-pages-lib/utils/featureUtils';
import { ENTERPRISE, FREE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { NavItemEnum } from '../../../constants/routing/NavItems';
import { useFeaturesForApiNames } from '../../../hooks/useFeatures';
import { useErrorBoundary } from '../../error-boundary/useErrorBoundary';
import withErrorBoundary from '../../error-boundary/withErrorBoundary';
import { PinnedFeaturesConfig } from '../PinnedFeaturesConfig';
import SuiteFeatureTableHeaderCell from '../suite-feature-table/SuiteFeatureTableHeaderCell';
import SmartCrmFeatureTableHeaderCell from './SmartCrmFeatureTableHeaderCell';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SMART_CRM_LEVEL_TO_API_NAME = {
  [FREE]: 'core-seats-free',
  // doesn't exist in merchandise, but can still query features for it
  [STARTER]: CORE_SEATS_STARTER,
  [PROFESSIONAL]: CORE_SEATS_PROFESSIONAL,
  [ENTERPRISE]: CORE_SEATS_ENTERPRISE
};
const buildFeatureTableHeaderData = ({
  productLevels,
  screen
}) => {
  return productLevels.reduce((headers, level) => {
    headers[SMART_CRM_LEVEL_TO_API_NAME[level]] = {
      Content: level === FREE || level === STARTER ? /*#__PURE__*/_jsx(SuiteFeatureTableHeaderCell, {
        productLevel: level,
        screen: screen
      }, `${SMART_CRM_LEVEL_TO_API_NAME[level]}-header-cell`) : /*#__PURE__*/_jsx(SmartCrmFeatureTableHeaderCell, {
        apiName: SMART_CRM_LEVEL_TO_API_NAME[level],
        screen: screen
      }, `${SMART_CRM_LEVEL_TO_API_NAME[level]}-header-cell`)
    };
    return headers;
  }, {});
};
const buildFeatureTableBodyData = ({
  features,
  productLevels,
  screen,
  staticDisplayRowCount
}) => {
  return {
    sharedData: {
      subscreen: screen,
      staticDisplayRowCount
    },
    sectionData: {
      'smart-crm': {
        apiNames: productLevels.map(level => SMART_CRM_LEVEL_TO_API_NAME[level]),
        features,
        LabelContent: /*#__PURE__*/_jsx(_Fragment, {})
      }
    }
  };
};
const SmartCrmFeatureTable = ({
  productLevels,
  screen
}) => {
  var _PinnedFeaturesConfig;
  const highlightedProducts = useMemo(() => productLevels.map(productLevel => SMART_CRM_LEVEL_TO_API_NAME[productLevel]), [productLevels]);
  const {
    features,
    loading,
    error
  } = useFeaturesForApiNames(highlightedProducts);
  const pinnedFeatures = useMemo(() => {
    if (!features || !features.length) return [];
    return pinKeyFeatures(features, NavItemEnum.SMART_CRM, PinnedFeaturesConfig);
  }, [features]);
  const {
    showBoundary
  } = useErrorBoundary();
  if (loading) return /*#__PURE__*/_jsx(LoadingSpinner, {
    grow: true
  });
  if (error) {
    showBoundary(new CustomError('Error fetching features', {
      error,
      productLevels,
      screen
    }));
  }
  if (!features || !features.length) return null;
  const tableHeader = buildFeatureTableHeaderData({
    productLevels,
    screen
  });
  const tableBody = buildFeatureTableBodyData({
    features: pinnedFeatures,
    productLevels,
    screen,
    staticDisplayRowCount: (_PinnedFeaturesConfig = PinnedFeaturesConfig[NavItemEnum.SMART_CRM]) === null || _PinnedFeaturesConfig === void 0 ? void 0 : _PinnedFeaturesConfig.length
  });
  return /*#__PURE__*/_jsx(FeatureTable, {
    headerData: tableHeader,
    bodyData: tableBody,
    canViewNewSeats: false
  });
};
export default withErrorBoundary(SmartCrmFeatureTable, {
  fallback: null,
  errorBoundaryName: 'SMART_CRM_FEATURE_TABLE'
});